import { SensorConfig, SensorData, SensorReading, SensorType, AnomalyEvent, BiometricSignature } from './sensor-types';

export class SensorIntegrationManager {
  private sensors: Map<string, SensorConfig> = new Map();
  private sensorData: Map<string, SensorData> = new Map();
  private callbacks: Map<string, Function[]> = new Map();

  async connectSensor(config: SensorConfig): Promise<boolean> {
    try {
      // Simulate WebUSB/WebBluetooth connection
      if ('navigator' in globalThis && 'usb' in navigator) {
        // Real USB connection would go here
        console.log(`Connecting to ${config.type} sensor: ${config.name}`);
      }
      
      config.connected = true;
      this.sensors.set(config.id, config);
      this.sensorData.set(config.id, {
        sensorId: config.id,
        readings: [],
        lastUpdate: Date.now(),
        anomalies: []
      });
      
      this.startDataStream(config.id);
      return true;
    } catch (error) {
      console.error('Sensor connection failed:', error);
      return false;
    }
  }

  private startDataStream(sensorId: string) {
    const config = this.sensors.get(sensorId);
    if (!config) return;

    const interval = 1000 / config.sampleRate;
    
    setInterval(() => {
      const reading = this.generateReading(config);
      this.addReading(sensorId, reading);
      this.detectAnomalies(sensorId, reading);
    }, interval);
  }

  private generateReading(config: SensorConfig): SensorReading {
    const now = Date.now();
    let value = 0;
    
    switch (config.type) {
      case SensorType.HEART_RATE:
        value = 70 + 10 * Math.sin(now / 1000) + Math.random() * 5;
        break;
      case SensorType.EEG:
        value = Math.sin(now / 100) * 50 + Math.random() * 20;
        break;
      case SensorType.GSR:
        value = 2.5 + 0.5 * Math.sin(now / 2000) + Math.random() * 0.3;
        break;
    }
    
    return {
      timestamp: now,
      value,
      quality: 0.8 + Math.random() * 0.2,
      raw: value + Math.random() * 2
    };
  }

  private addReading(sensorId: string, reading: SensorReading) {
    const data = this.sensorData.get(sensorId);
    if (!data) return;

    data.readings.push(reading);
    if (data.readings.length > 1000) {
      data.readings = data.readings.slice(-1000);
    }
    data.lastUpdate = reading.timestamp;

    this.notifyCallbacks(sensorId, reading);
  }

  private detectAnomalies(sensorId: string, reading: SensorReading) {
    const data = this.sensorData.get(sensorId);
    if (!data || data.readings.length < 10) return;

    const recent = data.readings.slice(-10);
    const avg = recent.reduce((sum, r) => sum + r.value, 0) / recent.length;
    const deviation = Math.abs(reading.value - avg) / avg;

    if (deviation > 0.3) {
      const anomaly: AnomalyEvent = {
        timestamp: reading.timestamp,
        type: 'spike',
        severity: Math.min(deviation, 1),
        description: `Unusual ${sensorId} reading: ${reading.value.toFixed(2)}`
      };
      data.anomalies.push(anomaly);
    }
  }

  subscribe(sensorId: string, callback: Function) {
    if (!this.callbacks.has(sensorId)) {
      this.callbacks.set(sensorId, []);
    }
    this.callbacks.get(sensorId)!.push(callback);
  }

  private notifyCallbacks(sensorId: string, reading: SensorReading) {
    const callbacks = this.callbacks.get(sensorId) || [];
    callbacks.forEach(cb => cb(reading));
  }

  getSensorData(sensorId: string): SensorData | undefined {
    return this.sensorData.get(sensorId);
  }

  generateBiometricSignature(): BiometricSignature {
    const hrData = this.sensorData.get('heart_rate');
    const eegData = this.sensorData.get('eeg');
    const gsrData = this.sensorData.get('gsr');

    const hrv = hrData ? this.calculateHRV(hrData.readings) : 0;
    const eegCoherence = eegData ? this.calculateCoherence(eegData.readings) : 0;
    const gsrStability = gsrData ? this.calculateStability(gsrData.readings) : 0;

    const overallCoherence = (hrv + eegCoherence + gsrStability) / 3;
    const spiritualAlignment = Math.min(overallCoherence * 1.2, 1);

    return {
      heartRateVariability: hrv,
      eegCoherence,
      gsrStability,
      overallCoherence,
      spiritualAlignment
    };
  }

  private calculateHRV(readings: SensorReading[]): number {
    if (readings.length < 10) return 0;
    const recent = readings.slice(-20);
    const intervals = recent.slice(1).map((r, i) => r.timestamp - recent[i].timestamp);
    const avg = intervals.reduce((sum, i) => sum + i, 0) / intervals.length;
    const variance = intervals.reduce((sum, i) => sum + Math.pow(i - avg, 2), 0) / intervals.length;
    return Math.min(Math.sqrt(variance) / avg, 1);
  }

  private calculateCoherence(readings: SensorReading[]): number {
    if (readings.length < 20) return 0;
    const values = readings.slice(-20).map(r => r.value);
    const fft = this.simpleFFT(values);
    return Math.min(fft.reduce((sum, val) => sum + Math.abs(val), 0) / fft.length / 100, 1);
  }

  private calculateStability(readings: SensorReading[]): number {
    if (readings.length < 10) return 0;
    const recent = readings.slice(-10);
    const avg = recent.reduce((sum, r) => sum + r.value, 0) / recent.length;
    const variance = recent.reduce((sum, r) => sum + Math.pow(r.value - avg, 2), 0) / recent.length;
    return Math.max(0, 1 - Math.sqrt(variance) / avg);
  }

  private simpleFFT(values: number[]): number[] {
    // Simplified FFT approximation for coherence calculation
    return values.map((val, i) => Math.sin(i * Math.PI / values.length) * val);
  }
}

export const sensorManager = new SensorIntegrationManager();